/* Settings: organization, code providers, members, review agent, billing.

   The review-agent section writes to two tables through one endpoint. What the review
   *does* -- strictness, triggers, filters -- belongs to the engine; what the published
   comment looks like belongs to the platform. The split matters when reading the code and
   not at all when using the screen, which is why it is one form. */

import { useEffect, useState } from "react";
import { NavLink, useParams } from "react-router-dom";

import { ROOT, api } from "@/api";
import { useOrgPath, useSession } from "@/session";
import { useApi } from "@/useApi";
import { Async, Button, Panel, Toggle, field, panel, primaryButton, relativeTime } from "@/ui";

/* The prototype's settings row: flat inside the panel, divided by a hairline of
   --surface-elevated, label and description on the left and the control on the right. */
function SetRow({ title, detail, last, children }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "16px",
        flexWrap: "wrap",
        padding: "18px 20px",
        borderBottom: last ? "none" : "1px solid var(--surface-elevated)",
      }}
    >
      <span style={{ flex: 1, minWidth: "220px" }}>
        <span
          style={{
            display: "block",
            font: "var(--text-body-14)",
            color: "var(--text-primary)",
            marginBottom: "3px",
          }}
        >
          {title}
        </span>
        <span style={{ font: "var(--text-caption)", color: "var(--text-muted)" }}>{detail}</span>
      </span>
      {children}
    </div>
  );
}

function Labelled({ label, error, children }) {
  return (
    <label style={{ display: "grid", gap: "6px" }}>
      <span style={{ font: "var(--text-caption)", color: "var(--text-muted)" }}>{label}</span>
      {children}
      {error && (
        <span style={{ font: "var(--text-caption)", color: "var(--danger)" }}>{error}</span>
      )}
    </label>
  );
}

const SECTIONS = [
  ["organisation", "Organisation"],
  ["providers", "Code providers"],
  ["members", "Members"],
  ["agent", "Review agent"],
  ["billing", "Billing"],
];

// Only High can be chosen for now: it is the strictness the review engine was benchmarked at.
const STRICTNESS = [
  ["low", "Low — only critical findings (coming soon)"],
  ["medium", "Medium — only real defects (coming soon)"],
  ["high", "High — the product default; everything, including nits"],
];

export function Settings() {
  const { section = "organisation" } = useParams();
  return (
    <div style={{ display: "flex", gap: "24px", alignItems: "flex-start" }}>
      <nav style={{ display: "grid", gap: "2px", minWidth: "170px", position: "sticky", top: "116px" }}>
        {SECTIONS.map(([key, label]) => (
          <NavLink
            key={key}
            to={`/settings/${key}`}
            style={({ isActive }) => ({
              position: "relative",
              font: "var(--text-body-14)",
              textDecoration: "none",
              padding: "10px 13px",
              borderRadius: "9px",
              color: isActive ? "var(--text-primary)" : "var(--text-muted)",
              background: isActive ? "var(--surface-elevated)" : "transparent",
              transition: "background 240ms var(--ease-out), color 240ms var(--ease-out)",
            })}
          >
            {({ isActive }) => (
              <>
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: 0,
                    top: "9px",
                    bottom: "9px",
                    width: "2px",
                    borderRadius: "999rem",
                    background: "var(--text-primary)",
                    transformOrigin: "top",
                    transform: isActive ? "scaleY(1)" : "scaleY(0)",
                    transition: "transform 300ms var(--ease-out)",
                  }}
                />
                {label}
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <div style={{ flex: 1, minWidth: 0, display: "grid", gap: "20px" }}>
        {section === "organisation" && <OrganisationSection />}
        {section === "providers" && <ProvidersSection />}
        {section === "members" && <MembersSection />}
        {section === "agent" && <AgentSection />}
        {section === "billing" && <BillingSection />}
      </div>
    </div>
  );
}

function useSettings() {
  const orgPath = useOrgPath();
  return useApi((signal) => api.get(`${orgPath}/settings`, { signal }), [orgPath], {
    skip: !orgPath,
  });
}

function OrganisationSection() {
  const orgPath = useOrgPath();
  const { refresh } = useSession();
  const settings = useSettings();
  const [name, setName] = useState("");
  const [branch, setBranch] = useState("main");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (settings.data) setName(settings.data.organization.name);
  }, [settings.data]);

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setSaved(false);
    try {
      await api.patch(`${orgPath}/settings`, { name, defaultBranch: branch });
      await refresh();
      setSaved(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Organisation">
      <Async state={settings}>
        {() => (
          <form onSubmit={save} noValidate>
            <SetRow title="Organisation name" detail="Shown on reviews and in the dashboard">
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                style={field}
              />
            </SetRow>
            <SetRow
              title="Default branch"
              detail="Reviews run against pull requests targeting this branch"
            >
              <input
                value={branch}
                onChange={(event) => setBranch(event.target.value)}
                style={field}
              />
            </SetRow>
            <SetRow last title="Save changes" detail="Applies to every repository here">
              <span style={{ display: "inline-flex", alignItems: "center", gap: "12px" }}>
                {saved && (
                  <span style={{ font: "var(--text-caption)", color: "var(--success)" }}>Saved</span>
                )}
                <button type="submit" disabled={busy} style={primaryButton}>
                  {busy ? "Saving…" : "Save"}
                </button>
              </span>
            </SetRow>
          </form>
        )}
      </Async>
    </Panel>
  );
}

function ProvidersSection() {
  const orgPath = useOrgPath();
  const { organization } = useSession();
  /* Installing the App is `repository.manage`, an administrator's permission. A member
     shown the link would land on the API's refusal, so they are told who can instead. */
  const canManage = organization?.role === "org_admin";
  const providers = useApi(
    (signal) => api.get(`${orgPath}/integrations`, { signal }),
    [orgPath],
    { skip: !orgPath },
  );
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState("");

  /* Frees the installation so it can be connected to another workspace. Its repositories
     stop being reviewed here, which is why it asks first. */
  async function disconnectGitHub(row) {
    const confirmed = window.confirm(
      `Disconnect GitHub from this workspace? Reviews stop for ${row.repositoryCount} repositories until it is connected again.`,
    );
    if (!confirmed) return;
    setDisconnecting(true);
    setDisconnectError("");
    try {
      const linked = await api.get(`${orgPath}/github/installations/linked`);
      for (const item of linked) {
        await api.del(`${orgPath}/github/installations/${item.installationId}`);
      }
      providers.reload();
    } catch (failure) {
      setDisconnectError(failure.message);
    } finally {
      setDisconnecting(false);
    }
  }

  return (
    <Panel title="Code providers">
      <Async state={providers}>
        {(rows) => (
          <div style={{ display: "grid", gap: "10px" }}>
            {disconnectError && (
              <p style={{ font: "var(--text-caption)", color: "var(--danger)", margin: 0 }}>{disconnectError}</p>
            )}
            {rows.map((row) => (
              <div
                key={row.provider}
                style={{
                  ...panel,
                  background: "var(--surface-elevated)",
                  padding: "14px 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "16px",
                }}
              >
                <div>
                  <strong style={{ font: "var(--text-body-14)", color: "var(--text-primary)" }}>
                    {row.displayName}
                  </strong>
                  <p style={{ font: "var(--text-caption)", color: "var(--text-muted)", margin: "4px 0 0" }}>
                    {row.connected
                      ? `${row.repositoryCount} repositories connected via ${row.accounts.join(", ")}`
                      : "Not connected"}
                  </p>
                </div>
                {row.provider === "github" && !canManage ? (
                  <span style={{ font: "var(--text-caption)", color: "var(--text-disabled)" }}>
                    Only admins can manage repositories
                  </span>
                ) : row.provider === "github" ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                    {row.connected && (
                      <Button tone="secondary" disabled={disconnecting} onClick={() => disconnectGitHub(row)}>
                        Disconnect
                      </Button>
                    )}
                    <a
                      href={`${ROOT}/api/v1${orgPath}/github/install`}
                      style={{ ...primaryButton, textDecoration: "none" }}
                    >
                      {row.connected ? "Manage" : "Connect"}
                    </a>
                  </span>
                ) : (
                  <span style={{ font: "var(--text-caption)", color: "var(--text-disabled)" }}>
                    Coming soon
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </Async>
    </Panel>
  );
}

function MembersSection() {
  const orgPath = useOrgPath();
  const members = useApi((signal) => api.get(`${orgPath}/members`, { signal }), [orgPath], {
    skip: !orgPath,
  });
  const [email, setEmail] = useState("");
  const [fields, setFields] = useState({});
  const [message, setMessage] = useState("");
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);

  async function invite(event) {
    event.preventDefault();
    setBusy(true);
    setFields({});
    setMessage("");
    setIssued(null);
    try {
      const created = await api.post(`${orgPath}/invitations`, { email, role: "member" });
      setEmail("");
      /* The link is readable exactly once, here. Only its digest reaches the database,
         so nothing can show it again after this render. */
      setIssued(created);
      members.reload();
    } catch (error) {
      setFields(error.fields ?? {});
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Members">
      <form onSubmit={invite} noValidate style={{ display: "flex", gap: "10px", alignItems: "flex-end", marginBottom: "18px" }}>
        <div style={{ flex: 1, maxWidth: "320px" }}>
          <Labelled label="Invite by email" error={fields.email}>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              style={{ ...field, width: "100%", boxSizing: "border-box" }}
            />
          </Labelled>
        </div>
        <Button type="submit" disabled={busy}>
          {busy ? "Inviting…" : "Invite"}
        </Button>
      </form>
      {message && (
        <p style={{ font: "var(--text-caption)", color: "var(--danger)" }}>{message}</p>
      )}
      {issued && (
        <p
          style={{
            font: "var(--text-caption)",
            color: "var(--text-secondary)",
            background: "var(--surface-elevated)",
            border: "1px solid var(--border)",
            borderRadius: "10px",
            padding: "12px 14px",
            margin: "0 0 14px",
            wordBreak: "break-word",
          }}
        >
          Invited {issued.email}. Send them this link — it works once and expires in
          fourteen days: <code>{issued.inviteUrl}</code>
        </p>
      )}

      <Async state={members}>
        {(data) => (
          <>
            <div style={{ display: "grid", gap: "8px" }}>
              {data.members.map((member) => (
                <Row
                  key={member.id}
                  title={member.name || member.email}
                  detail={member.email}
                  trailing={member.role === "org_admin" ? "Admin" : "Member"}
                />
              ))}
            </div>
            {data.invitations.length > 0 && (
              <>
                <h3 style={{ font: "var(--text-caption)", color: "var(--text-muted)", margin: "18px 0 8px" }}>
                  Pending invitations
                </h3>
                <div style={{ display: "grid", gap: "8px" }}>
                  {data.invitations.map((invite) => (
                    <Row
                      key={invite.id}
                      title={invite.email}
                      detail={`Expires ${relativeTime(invite.expiresAt)}`}
                      trailing={invite.role === "org_admin" ? "Admin" : "Member"}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </Async>
    </Panel>
  );
}

function Row({ title, detail, trailing }) {
  return (
    <SetRow title={title} detail={detail}>
      <span style={{ font: "var(--text-body-14)", color: "var(--text-primary)" }}>{trailing}</span>
    </SetRow>
  );
}

function AgentSection() {
  const orgPath = useOrgPath();
  const settings = useSettings();
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  async function patch(body) {
    setBusy(true);
    setSaved(false);
    try {
      await api.patch(`${orgPath}/settings/review-agent`, body);
      await settings.reload();
      setSaved(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Review agent">
      <Async state={settings}>
        {(data) => {
          const agent = data.reviewAgent;
          return (
            <div style={{ display: "grid", gap: "18px" }}>
              <label style={{ display: "grid", gap: "6px", maxWidth: "420px" }}>
                <span style={{ font: "var(--text-caption)", color: "var(--text-muted)" }}>
                  Sensitivity
                </span>
                <select
                  value={agent.strictness}
                  disabled={busy}
                  onChange={(event) => patch({ strictness: event.target.value })}
                  style={{
                    font: "var(--text-body-14)",
                    color: "var(--text-primary)",
                    background: "var(--surface-elevated)",
                    border: "1px solid var(--border)",
                    borderRadius: "8px",
                    padding: "10px",
                  }}
                >
                  {STRICTNESS.map(([value, label]) => (
                    <option key={value} value={value} disabled={value !== "high"}>
                      {label}
                    </option>
                  ))}
                </select>
                <span style={{ font: "var(--text-caption)", color: "var(--text-disabled)" }}>
                  Sensitivity decides which findings are published, not how hard the reviewer
                  looks. Suppressed findings are still stored and shown here.
                </span>
              </label>

              <Switch
                label="Re-review on every new commit"
                checked={agent.reviewOnUpdate}
                busy={busy}
                onChange={(value) => patch({ reviewOnUpdate: value })}
              />
              <Switch
                label="Review draft pull requests"
                checked={agent.reviewDrafts}
                busy={busy}
                onChange={(value) => patch({ reviewDrafts: value })}
              />
              <Switch
                label="Comment outside the diff"
                checked={agent.outsideDiffComments}
                busy={busy}
                onChange={(value) => patch({ outsideDiffComments: value })}
              />
              <Switch
                label="Include a prompt for AI agents"
                checked={agent.promptToFix}
                busy={busy}
                onChange={(value) => patch({ promptToFix: value })}
              />
              <Switch
                label="Learn from our reactions"
                checked={agent.learningEnabled}
                busy={busy}
                onChange={(value) => patch({ learningEnabled: value })}
              />

              <div>
                <h3 style={{ font: "var(--text-caption)", color: "var(--text-muted)", margin: "0 0 8px" }}>
                  Summary sections
                </h3>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                  {[
                    ["summary", "Summary", "summaryProse"],
                    ["score", "Confidence score", "summaryScore"],
                    ["table", "Issue table", "summaryTable"],
                    ["diagram", "Sequence diagram", "summaryDiagram"],
                  ].map(([key, label, field]) => (
                    <label
                      key={key}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        font: "var(--text-caption)",
                        color: "var(--text-secondary)",
                      }}
                    >
                      <Toggle
                        checked={agent.summaryComponents.includes(key)}
                        disabled={busy}
                        label={label}
                        onChange={(value) => patch({ [field]: value })}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h3 style={{ font: "var(--text-caption)", color: "var(--text-muted)", margin: "0 0 8px" }}>
                  Filters
                </h3>
                <PatternList
                  label="Excluded branches"
                  values={agent.excludeBranches}
                  busy={busy}
                  onChange={(values) => patch({ excludeBranches: values })}
                />
                <PatternList
                  label="Ignored paths"
                  values={agent.ignorePatterns}
                  busy={busy}
                  onChange={(values) => patch({ ignorePatterns: values })}
                />
                <PatternList
                  label="Excluded bots"
                  values={agent.excludedBots}
                  busy={busy}
                  onChange={(values) => patch({ excludedBots: values })}
                />
              </div>

              {saved && (
                <span style={{ font: "var(--text-caption)", color: "var(--success)" }}>Saved</span>
              )}
            </div>
          );
        }}
      </Async>
    </Panel>
  );
}

function Switch({ label, checked, onChange, busy }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        font: "var(--text-body-14)",
        color: "var(--text-secondary)",
      }}
    >
      <Toggle checked={checked} disabled={busy} label={label} onChange={onChange} />
      {label}
    </label>
  );
}

/* Comma-separated because that is how people type a handful of globs, and a chip editor
   for three values would be more code than the feature. */
function PatternList({ label, values, onChange, busy }) {
  const [text, setText] = useState(values.join(", "));
  useEffect(() => setText(values.join(", ")), [values]);
  return (
    <label style={{ display: "grid", gap: "6px", marginBottom: "12px", maxWidth: "520px" }}>
      <span style={{ font: "var(--text-caption)", color: "var(--text-muted)" }}>{label}</span>
      <input
        value={text}
        disabled={busy}
        placeholder="release/*, vendor/**"
        onChange={(event) => setText(event.target.value)}
        onBlur={() => {
          const next = text.split(",").map((item) => item.trim()).filter(Boolean);
          if (next.join("\u0000") !== values.join("\u0000")) onChange(next);
        }}
        style={{
          font: "var(--text-body-14)",
          color: "var(--text-primary)",
          background: "var(--surface-elevated)",
          border: "1px solid var(--border)",
          borderRadius: "8px",
          padding: "9px 12px",
        }}
      />
    </label>
  );
}

function BillingSection() {
  const orgPath = useOrgPath();
  const billing = useApi((signal) => api.get(`${orgPath}/billing`, { signal }), [orgPath], {
    skip: !orgPath,
  });

  return (
    <Panel title="Billing">
      <Async state={billing}>
        {(data) => (
          <div style={{ display: "grid", gap: "14px", maxWidth: "480px" }}>
            <Row title="Plan" detail={`Billing period from ${relativeTime(data.periodStart)}`} trailing={data.plan} />
            <Row
              title="Seats"
              detail="Charged per active reviewer in this period"
              trailing={String(data.seats)}
            />
            <Row title="Review units used" detail="This billing period" trailing={String(data.unitsUsed)} />
            <p style={{ font: "var(--text-caption)", color: "var(--text-muted)", margin: 0 }}>
              A seat is a developer whose pull request CodeNerve actually reviewed, not a member
              of the organisation. Skipped and failed reviews are not billed.
            </p>
          </div>
        )}
      </Async>
    </Panel>
  );
}
